'use client';

import * as React from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import { SetupUpsellOfferCreateForm } from '@/components/setup/experience/steps/SetupUpsellOfferCreateForm';
import { SetupUpsellOfferRow } from '@/components/setup/experience/steps/SetupUpsellOfferRow';
import { Button } from '@/components/ui/button';
import { useDeleteOffers } from '@/lib/api/offers';

interface SetupCheckoutUpsellOfferStepProps {
  storeId: string;
  selectedOfferIds: string[];
  onChange: (offerIds: string[]) => void;
  onContinue: () => void;
  onBack: () => void;
}

/**
 * Guided funnel-generation sub-step shown only when the merchant opted into an
 * upsell. They create one or more offers (product + price) that the deployed
 * offer step binds to. At least one offer is required to continue, so the
 * upsell can never ship unconfigured.
 */
export function SetupCheckoutUpsellOfferStep({
  storeId,
  selectedOfferIds,
  onChange,
  onContinue,
  onBack,
}: SetupCheckoutUpsellOfferStepProps) {
  const { t } = useTranslation('setup');
  const deleteOffers = useDeleteOffers();
  const [isAdding, setIsAdding] = React.useState(selectedOfferIds.length === 0);
  const [removingId, setRemovingId] = React.useState<string | null>(null);

  const handleCreated = (offerId: string) => {
    onChange([...selectedOfferIds, offerId]);
    setIsAdding(false);
  };

  const handleRemove = async (offerId: string) => {
    setRemovingId(offerId);
    try {
      await deleteOffers.mutateAsync({ storeId, offerIds: [offerId] });
    } catch {
      // Error surfaced via the mutation hook's toast — still unbind locally.
    } finally {
      setRemovingId(null);
    }
    const next = selectedOfferIds.filter((id) => id !== offerId);
    onChange(next);
    if (next.length === 0) setIsAdding(true);
  };

  return (
    <SetupStepSection
      title={t('experience.checkout.upsellOffer.title')}
      description={t('experience.checkout.upsellOffer.description')}
      footer={
        <SetupStepFooter
          backLabel={t('footer.back')}
          onBack={onBack}
          primaryLabel={t('footer.continue')}
          onPrimary={onContinue}
          primaryDisabled={selectedOfferIds.length === 0}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {selectedOfferIds.length > 0 ? (
          <div className="flex flex-col gap-2">
            {selectedOfferIds.map((offerId) => (
              <SetupUpsellOfferRow
                key={offerId}
                offerId={offerId}
                onRemove={() => handleRemove(offerId)}
                isRemoving={removingId === offerId}
              />
            ))}
          </div>
        ) : null}

        {isAdding ? (
          <SetupUpsellOfferCreateForm
            storeId={storeId}
            onCreated={handleCreated}
            onCancel={selectedOfferIds.length > 0 ? () => setIsAdding(false) : undefined}
          />
        ) : (
          <Button type="button" variant="outline" onClick={() => setIsAdding(true)} className="gap-2 self-start">
            <Plus className="size-4" aria-hidden />
            {t('experience.checkout.upsellOffer.addAnother')}
          </Button>
        )}
      </div>
    </SetupStepSection>
  );
}
